import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  List,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../components/hooks/useAuth';
import { selectTeams } from '../store/selectors/teams.selectors';

import { setGlobalTeam } from '../reducers/team.reducer';
import type { GlobalTeam } from '../store/types/global-teams.types';
import { ALL_TEAM, fetchMyTeams, myTeamsQueryKey } from './useMyTeams';

const GlobalTeamsMenu = () => {
  const dispatch = useDispatch();

  const buttonClasses = useButtonStyles();
  const textFieldClasses = useTextFieldStyles();
  const storedTeams = useSelector(selectTeams);
  const auth = useAuth();

  const [searchValue, setSearchValue] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState(null);

  const isAccountScoped = auth.isAccountScoped;

  const { data: teams } = useTeams(isAccountScoped, searchValue, {
    select: (nextTeams: GlobalTeam[]) => nextTeams.map(transformTeamToOption),
    placeholderData: storedTeams.cache,
  });

  const handleClick = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setSearchValue('');
    setTimeout(() => document.getElementById('teams-search')?.focus(), 0);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = event.target.value;
    setSearchValue(text);
  };

  const open = Boolean(anchorEl);
  const anchorId = open ? 'team-popper' : undefined;

  function handleTeamChange(nextTeam: GlobalTeam) {
    return () => {
      setAnchorEl(null);
      dispatch(
        setGlobalTeam({
          id: nextTeam.id,
          name: nextTeam.name,
        })
      );
    };
  }

  return (
    <Box minWidth={170} maxWidth={200}>
      <Button
        onClick={handleClick}
        variant="outlined"
        classes={buttonClasses}
        endIcon={<FontAwesomeIcon icon={faChevronDown} />}
      >
        <span className={buttonClasses.text}>
          Team | {storedTeams.active?.name}
        </span>
      </Button>

      <Popper
        id={anchorId}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper elevation={8}>
            <Box width={250}>
              <Box p={1}>
                <TextField
                  id="teams-search"
                  classes={textFieldClasses}
                  onChange={handleChange}
                  placeholder="Search teams"
                  value={searchValue}
                  fullWidth
                />
              </Box>

              <Divider sx={{ opacity: 0.5 }} />
              <Box p={1}>
                <List style={{ overflowY: 'scroll' }}>
                  {!_.isEmpty(teams) ? (
                    teams.map((team: GlobalTeam) => (
                      <MenuItem
                        value={team.id}
                        key={team.id}
                        onClick={handleTeamChange(team)}
                      >
                        <Typography
                          className="tertia-text"
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {team.name}
                        </Typography>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem>
                      <Typography
                        className="tertia-text"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        No Teams Available
                      </Typography>
                    </MenuItem>
                  )}
                </List>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default GlobalTeamsMenu;

const useTeams = (isAccountScoped: boolean, search: string, options = {}) => {
  return useQuery(
    myTeamsQueryKey(search, isAccountScoped),
    () => fetchMyTeams(search, isAccountScoped),
    options
  );
};

function transformTeamToOption(team: GlobalTeam) {
  return {
    meta: team,
    name: team.name,
    id: team.id,
  };
}

const useButtonStyles = makeStyles({
  root: {
    width: '100%',
    padding: '4px 4px 4px 8px',
    justifyContent: 'start',
    position: 'relative',
    whiteSpace: 'nowrap',
  },

  endIcon: {
    marginRight: 4,
    position: 'absolute',
    top: '50%',
    right: 0,
    transform: 'translateY(-50%)',

    '& svg': {
      fontSize: '14px !important',
    },
  },

  text: {
    maxWidth: 'calc(100% - 25px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

const useTextFieldStyles = makeStyles(({ palette }: any) => ({
  root: {
    '& input:focus': {
      backgroundColor: palette.button.background2,
      color: palette.primary.contrastText,
    },

    '& input': {
      padding: '0.8rem !important',
    },
  },
}));

export { ALL_TEAM };
